import React, { useMemo, useState } from "react";

const SECTION_STYLES = {
  page: {
    display: "grid",
    gap: 0,
  },
  sectionCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 0,
    padding: "28px 32px",
    display: "grid",
    gap: 20,
    borderBottom: "1px solid #e2e8f0",
  },
  sectionCardFirst: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px 12px 0 0",
    padding: "28px 32px",
    display: "grid",
    gap: 20,
    borderBottom: "1px solid #e2e8f0",
  },
  sectionCardLast: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "0 0 12px 12px",
    padding: "28px 32px",
    display: "grid",
    gap: 20,
    boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    paddingBottom: 16,
    borderBottom: "1px solid #f1f5f9",
    marginBottom: 4,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.02em",
  },
  sectionSubtitle: {
    margin: "4px 0 0 0",
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1.5,
    maxWidth: 560,
  },
  formWrap: {
    display: "grid",
    gap: 0,
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "200px minmax(0, 1fr)",
    gap: 20,
    alignItems: "start",
    padding: "12px 0",
    borderBottom: "1px solid #f8fafc",
  },
  formLabel: {
    fontWeight: 600,
    color: "#374151",
    margin: 0,
    fontSize: 13,
    paddingTop: 10,
  },
  formHelper: {
    margin: "3px 0 0 0",
    fontSize: 11,
    color: "#94a3b8",
    lineHeight: 1.4,
  },
  actionRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    paddingTop: 20,
    borderTop: "1px solid #f1f5f9",
    marginTop: 8,
  },
  primaryButton: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 22px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    letterSpacing: "0.01em",
  },
  secondaryButton: {
    background: "#ffffff",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "10px 22px",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
  },
  dangerButton: {
    background: "transparent",
    color: "#dc2626",
    border: "1px solid #fca5a5",
    borderRadius: 8,
    padding: "7px 14px",
    fontWeight: 600,
    fontSize: 12,
    cursor: "pointer",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 1,
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    overflow: "hidden",
  },
  kpiCard: {
    background: "#fff",
    padding: "16px 20px",
    display: "grid",
    gap: 4,
    borderRight: "1px solid #e2e8f0",
  },
  kpiLabel: {
    margin: 0,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontWeight: 700,
    color: "#94a3b8",
  },
  kpiValue: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: "#0f172a",
  },
};

function numberOrZero(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return 0;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function addDaysToDate(dateString, days) {
  if (!dateString) return "";
  const base = new Date(dateString);
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + numberOrZero(days));
  return base.toISOString().slice(0, 10);
}

function formatDate(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
}

function currency(value) {
  return `R ${numberOrZero(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getClaimNoticePeriodDays(contractType) {
  const type = String(contractType || "").toLowerCase();
  if (type.includes("fidic")) return 42;
  return 42;
}

function computeProposedRulingDate(claimDate) {
  if (!claimDate) return "";
  return addDaysToDate(claimDate, 28);
}

function getClaimDaysRemaining(notificationDate, claimDate, contractType) {
  if (!notificationDate || !claimDate) return null;
  const noticeDate = new Date(notificationDate);
  const submittedDate = new Date(claimDate);
  if (Number.isNaN(noticeDate.getTime()) || Number.isNaN(submittedDate.getTime())) return null;

  noticeDate.setHours(0, 0, 0, 0);
  submittedDate.setHours(0, 0, 0, 0);

  const allowedDays = getClaimNoticePeriodDays(contractType);
  const dueDate = new Date(noticeDate);
  dueDate.setDate(dueDate.getDate() + allowedDays);

  const diffMs = dueDate.getTime() - submittedDate.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function isClaimLate(notificationDate, claimDate, contractType) {
  const daysRemaining = getClaimDaysRemaining(notificationDate, claimDate, contractType);
  return daysRemaining !== null && daysRemaining < 0;
}

function getClaimTimeBarLabel(notificationDate, claimDate, contractType) {
  const daysRemaining = getClaimDaysRemaining(notificationDate, claimDate, contractType);
  if (daysRemaining === null) return "Waiting for dates";
  if (daysRemaining < 0) {
    return `Time barred by ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? "" : "s"}`;
  }
  return `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining`;
}

function getClaimCountdownStyle(daysRemaining) {
  if (daysRemaining === null) return {};
  if (daysRemaining < 0) {
    return { background: "#fef2f2", color: "#dc2626", fontWeight: 700 };
  }
  if (daysRemaining < 7) {
    return { background: "#fff7ed", color: "#ea580c", fontWeight: 700 };
  }
  return { background: "#ecfeff", color: "#155e75", fontWeight: 700 };
}

function buildNotificationArray(count, existing = []) {
  return Array.from({ length: count }, (_, index) => ({
    id: existing[index]?.id || `notice-${index + 1}`,
    label: existing[index]?.label || `Notification ${index + 1}`,
    notifyDate: existing[index]?.notifyDate || "",
    sent: existing[index]?.sent || "No",
  }));
}

function statusStyle(status) {
  const value = String(status || "");
  if (value === "Awarded") return { background: "#f0fdf4", color: "#15803d", fontWeight: 700, border: "1px solid #bbf7d0" };
  if (value === "Rejected") return { background: "#fef2f2", color: "#b91c1c", fontWeight: 700, border: "1px solid #fecaca" };
  if (value === "Time Barred") return { background: "#fef2f2", color: "#991b1b", fontWeight: 700, border: "1px solid #fca5a5" };
  return { background: "#eff6ff", color: "#1d4ed8", fontWeight: 700, border: "1px solid #bfdbfe" };
}

function ClaimsRow({ label, helper, children }) {
  return (
    <div style={SECTION_STYLES.formRow}>
      <div>
        <p style={SECTION_STYLES.formLabel}>{label}</p>
        {helper ? <p style={SECTION_STYLES.formHelper}>{helper}</p> : null}
      </div>
      <div>{children}</div>
    </div>
  );
}

export const emptyClaimForm = {
  projectId: "",
  claimNo: "",
  title: "",
  detail: "",
  claimType: "Cost and Time",
  latestNotificationDate: "",
  claimDate: "",
  proposedRulingDate: "",
  status: "In Review",
  causeEventDate: "",
  responsibleParty: "Employer",
  contractClause: "",
  supportingReference: "",
  daysClaimed: "",
  daysAwarded: "",
  pgRatePerDay: "",
  pgRateDay: "",
  pgClaimed: "",
  pgAwarded: "",
  directCostClaimed: "",
  directCostAwarded: "",
  engineerRecommendation: "",
  closeOutRemarks: "",
  notificationCount: "0",
  notifications: [],
};

function ClaimsSection({
  claimForm,
  setClaimForm,
  handleAddOrUpdateClaim,
  claims,
  currentProjectId,
  editingClaimId,
  handleEditClaim,
  handleDeleteClaim,
  clearClaimForm,
  handleViewItem,
  currentProject,
  certificates = [],
}) {
  const [claimSearch, setClaimSearch] = useState("");
  const [claimStatusFilter, setClaimStatusFilter] = useState("All");
  const [claimSortBy, setClaimSortBy] = useState("claimDate_desc");

  const contractType = currentProject?.contractType || "GCC";
  const noticePeriodDays = getClaimNoticePeriodDays(contractType);
  const estimatedEndDate =
    currentProject?.estimatedEndDate ||
    currentProject?.completionDate ||
    currentProject?.revisedEndDate ||
    "";

  const awardedDaysTotal = useMemo(() => {
    return (claims || [])
      .filter((item) => item.projectId === currentProjectId)
      .filter((item) => String(item.status || "") === "Awarded")
      .reduce((sum, item) => sum + numberOrZero(item.daysAwarded || item.daysClaimed), 0);
  }, [claims, currentProjectId]);

  const calculatedRevisedDate = useMemo(() => {
    return addDaysToDate(estimatedEndDate, awardedDaysTotal);
  }, [estimatedEndDate, awardedDaysTotal]);

  const projectClaims = useMemo(() => {
    return (claims || []).filter((item) => item.projectId === currentProjectId);
  }, [claims, currentProjectId]);

  const filteredClaims = useMemo(() => {
    const filtered = projectClaims.filter((item) => {
      const matchesStatus =
        claimStatusFilter === "All" || String(item.status || "") === claimStatusFilter;

      const haystack = [
        item.claimNo,
        item.title,
        item.detail,
        item.status,
        item.claimDate,
        item.latestNotificationDate,
        item.contractClause,
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !claimSearch.trim() || haystack.includes(claimSearch.toLowerCase());

      return matchesStatus && matchesSearch;
    });

    return [...filtered].sort((a, b) => {
      if (claimSortBy === "claimDate_asc") {
        return String(a.claimDate || "").localeCompare(String(b.claimDate || ""));
      }
      if (claimSortBy === "claimNo_asc") {
        return String(a.claimNo || "").localeCompare(String(b.claimNo || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      if (claimSortBy === "claimNo_desc") {
        return String(b.claimNo || "").localeCompare(String(a.claimNo || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      if (claimSortBy === "status_asc") {
        return String(a.status || "").localeCompare(String(b.status || ""));
      }
      if (claimSortBy === "status_desc") {
        return String(b.status || "").localeCompare(String(a.status || ""));
      }
      return String(b.claimDate || "").localeCompare(String(a.claimDate || ""));
    });
  }, [projectClaims, claimStatusFilter, claimSearch, claimSortBy]);

  const claimsDashboard = useMemo(() => {
    return {
      totalClaims: projectClaims.length,
      totalCostClaimed: projectClaims.reduce(
        (sum, item) =>
          sum +
          numberOrZero(item.pgClaimed) +
          numberOrZero(item.directCostClaimed),
        0
      ),
      totalTimeClaimed: projectClaims.reduce(
        (sum, item) => sum + numberOrZero(item.daysClaimed),
        0
      ),
      totalAwardedDays: projectClaims.reduce(
        (sum, item) => sum + numberOrZero(item.daysAwarded),
        0
      ),
      inReviewClaims: projectClaims.filter((item) => item.status === "In Review").length,
      awardedClaims: projectClaims.filter((item) => item.status === "Awarded").length,
      rejectedClaims: projectClaims.filter((item) => item.status === "Rejected").length,
      lateSubmissions: projectClaims.filter((item) =>
        isClaimLate(item.latestNotificationDate, item.claimDate, contractType)
      ).length,
      atRiskClaims: projectClaims.filter((item) => {
        const daysRemaining = getClaimDaysRemaining(
          item.latestNotificationDate,
          item.claimDate,
          contractType
        );
        return daysRemaining !== null && daysRemaining >= 0 && daysRemaining < 7;
      }).length,
      overdueRulings: projectClaims.filter((item) => {
        if (!item.proposedRulingDate || item.status !== "In Review") return false;
        const rulingDate = new Date(item.proposedRulingDate);
        const today = new Date();
        rulingDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return rulingDate < today;
      }).length,
      dueNotifications: projectClaims.reduce((sum, item) => {
        const validClaim =
          String(item.status || "") !== "Rejected" &&
          String(item.status || "") !== "Time Barred";
        if (!validClaim) return sum;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const notifications = Array.isArray(item.notifications) ? item.notifications : [];
        const dueCount = notifications.filter((notice) => {
          if (!notice?.notifyDate) return false;
          const notifyDate = new Date(notice.notifyDate);
          if (Number.isNaN(notifyDate.getTime())) return false;
          notifyDate.setHours(0, 0, 0, 0);
          return notifyDate <= today && String(notice.sent || "No") !== "Yes";
        }).length;
        return sum + dueCount;
      }, 0),
    };
  }, [projectClaims, contractType]);

  const noticeDaysRemaining = getClaimDaysRemaining(
    claimForm.latestNotificationDate,
    claimForm.claimDate,
    contractType
  );

  const notificationCount = Math.max(0, numberOrZero(claimForm.notificationCount));
  const notifications = Array.isArray(claimForm.notifications)
    ? claimForm.notifications
    : [];

  const updateNotificationCount = (value) => {
    const count = Math.max(0, numberOrZero(value));
    setClaimForm({
      ...claimForm,
      projectId: currentProjectId,
      notificationCount: String(count),
      notifications: buildNotificationArray(count, notifications),
    });
  };

  const updateNotificationField = (index, field, value) => {
    const nextNotifications = buildNotificationArray(notificationCount, notifications).map(
      (item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]: value,
            }
          : item
    );

    setClaimForm({
      ...claimForm,
      projectId: currentProjectId,
      notifications: nextNotifications,
    });
  };

  const claimStillValid =
    claimForm.status !== "Rejected" &&
    claimForm.status !== "Time Barred" &&
    !isClaimLate(claimForm.latestNotificationDate, claimForm.claimDate, contractType);

  return (
    <section style={SECTION_STYLES.page}>
      {/* ── HEADER CARD ─────────────────────────────────────────────────── */}
      <div style={{ ...SECTION_STYLES.sectionCardFirst, background: "#0f172a", color: "#f8fafc" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#3b82f6", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 6 }}>
              Claims Register · {currentProjectId}
            </div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#f8fafc", letterSpacing: "-0.03em" }}>
              Commercial Claims Administration
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
              Manage the full claim lifecycle — notice compliance, time-bar tracking, cost quantification and awarded extension of time.
            </p>
          </div>
          {editingClaimId ? (
            <button onClick={clearClaimForm} style={{ ...SECTION_STYLES.secondaryButton, background: "#1e293b", color: "#94a3b8", border: "1px solid #334155" }}>
              ✕ Clear Form
            </button>
          ) : null}
        </div>

        {/* KPI strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginTop: 4 }}>
          {[
            { label: "Total Claims", value: claimsDashboard.totalClaims, color: "#f8fafc", sub: "registered" },
            { label: "In Review", value: claimsDashboard.inReviewClaims, color: "#60a5fa", sub: "pending ruling" },
            { label: "Awarded", value: claimsDashboard.awardedClaims, color: "#34d399", sub: "concluded" },
            { label: "Rejected", value: claimsDashboard.rejectedClaims, color: "#f87171", sub: "concluded" },
            { label: "At Risk", value: claimsDashboard.atRiskClaims, color: "#fbbf24", sub: "< 7 days left" },
            { label: "Due Notifications", value: claimsDashboard.dueNotifications, color: "#fb923c", sub: "action required" },
          ].map(({ label, value, color, sub }) => (
            <div key={label} style={{ background: "#1e293b", borderRadius: 8, padding: "14px 16px", border: "1px solid #334155" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 26, fontWeight: 900, color, letterSpacing: "-0.04em", lineHeight: 1 }}>{value}</div>
              <div style={{ fontSize: 10, color: "#475569", marginTop: 3 }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Commercial summary bar */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, paddingTop: 14, borderTop: "1px solid #1e293b" }}>
          {[
            { label: "Total Cost Claimed", value: currency(claimsDashboard.totalCostClaimed), color: "#fb923c" },
            { label: "Days Claimed", value: claimsDashboard.totalTimeClaimed + " days", color: "#fbbf24" },
            { label: "Days Awarded", value: claimsDashboard.totalAwardedDays + " days", color: "#34d399" },
            { label: "Revised Completion", value: formatDate(calculatedRevisedDate), color: "#60a5fa" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ padding: "10px 14px", background: "#0a1628", borderRadius: 8, border: "1px solid #1e293b" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 15, fontWeight: 800, color, letterSpacing: "-0.02em" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CLAIM FORM ──────────────────────────────────────────────────── */}
      <div style={SECTION_STYLES.sectionCard}>
        <div style={SECTION_STYLES.sectionHeader}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 4 }}>
              {editingClaimId ? "Edit Claim" : "New Claim"}
            </div>
            <h3 style={SECTION_STYLES.sectionTitle}>
              {editingClaimId ? "Update Claim Record" : "Capture Claim"}
            </h3>
            <p style={SECTION_STYLES.sectionSubtitle}>
              Record the claim reference, legal basis, time impact, cost quantification and administration details.
            </p>
          </div>
        </div>

        <div style={SECTION_STYLES.formWrap}>
          {/* Group: Identification */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.12em", padding: "12px 0 4px", borderTop: "2px solid #f1f5f9", marginTop: 4 }}>
            Identification
          </div>
          <ClaimsRow label="Claim Number" helper="Use your formal claim reference.">
            <input
              type="text"
              value={claimForm.claimNo || ""}
              onChange={(e) =>
                setClaimForm({ ...claimForm, claimNo: e.target.value, projectId: currentProjectId })
              }
            />
          </ClaimsRow>

          <ClaimsRow label="Claim Title" helper="Short summary for dashboards and registers.">
            <input
              type="text"
              value={claimForm.title || ""}
              onChange={(e) =>
                setClaimForm({ ...claimForm, title: e.target.value, projectId: currentProjectId })
              }
            />
          </ClaimsRow>

          <ClaimsRow label="Claim Detail" helper="Detailed description of the cause and basis of the claim.">
            <textarea
              value={claimForm.detail || ""}
              onChange={(e) =>
                setClaimForm({ ...claimForm, detail: e.target.value, projectId: currentProjectId })
              }
            />
          </ClaimsRow>

          <ClaimsRow label="Claim Type" helper="Choose whether the claim is for time, cost, or both.">
            <select
              value={claimForm.claimType || "Cost and Time"}
              onChange={(e) =>
                setClaimForm({ ...claimForm, claimType: e.target.value, projectId: currentProjectId })
              }
            >
              <option value="Cost and Time">Cost and Time</option>
              <option value="Time Only">Time Only</option>
              <option value="Cost Only">Cost Only</option>
            </select>
          </ClaimsRow>

          {/* Group: Contract Administration */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.12em", padding: "12px 0 4px", borderTop: "2px solid #f1f5f9", marginTop: 8 }}>
            Contract Administration &amp; Notice Compliance
          </div>

          <ClaimsRow label="Cause Event Date" helper="Date on which the underlying event occurred.">
            <input
              type="date"
              value={claimForm.causeEventDate || ""}
              onChange={(e) =>
                setClaimForm({
                  ...claimForm,
                  causeEventDate: e.target.value,
                  projectId: currentProjectId,
                })
              }
            />
          </ClaimsRow>

          <ClaimsRow label="Latest Notification Date" helper={`Used as the time-bar start date for ${contractType} (${noticePeriodDays} days).`}>
            <input
              type="date"
              value={claimForm.latestNotificationDate || ""}
              onChange={(e) =>
                setClaimForm({
                  ...claimForm,
                  latestNotificationDate: e.target.value,
                  projectId: currentProjectId,
                })
              }
            />
          </ClaimsRow>

          <ClaimsRow label="Claim Submission Date" helper="Date the formal claim was submitted.">
            <input
              type="date"
              value={claimForm.claimDate || ""}
              onChange={(e) =>
                setClaimForm({
                  ...claimForm,
                  claimDate: e.target.value,
                  proposedRulingDate: computeProposedRulingDate(e.target.value),
                  projectId: currentProjectId,
                })
              }
              style={(() => {
                if (noticeDaysRemaining !== null && noticeDaysRemaining < 0) {
                  return { border: "2px solid #dc2626", background: "#fef2f2" };
                }
                if (noticeDaysRemaining !== null && noticeDaysRemaining < 7) {
                  return { border: "2px solid #ea580c", background: "#fff7ed" };
                }
                return {};
              })()}
            />
          </ClaimsRow>

          <ClaimsRow label="Time-Bar Status" helper="Live validation based on notification date and submission date.">
            <div
              style={{
                ...getClaimCountdownStyle(noticeDaysRemaining),
                padding: "10px 12px",
                borderRadius: 8,
                minHeight: 42,
                display: "flex",
                alignItems: "center",
              }}
            >
              {getClaimTimeBarLabel(
                claimForm.latestNotificationDate,
                claimForm.claimDate,
                contractType
              )}
            </div>
          </ClaimsRow>

          <ClaimsRow label="Proposed Ruling Date" helper="Auto-calculated from claim submission date.">
            <input type="date" value={claimForm.proposedRulingDate || ""} readOnly />
          </ClaimsRow>

          <ClaimsRow label="Status" helper="Current claim status in the administration workflow.">
            <select
              value={claimForm.status || "In Review"}
              onChange={(e) =>
                setClaimForm({ ...claimForm, status: e.target.value, projectId: currentProjectId })
              }
            >
              <option value="In Review">In Review</option>
              <option value="Awarded">Awarded</option>
              <option value="Rejected">Rejected</option>
              <option value="Time Barred">Time Barred</option>
            </select>
          </ClaimsRow>

          <ClaimsRow label="Responsible Party" helper="Who caused the event giving rise to the claim.">
            <input
              type="text"
              value={claimForm.responsibleParty || ""}
              onChange={(e) =>
                setClaimForm({
                  ...claimForm,
                  responsibleParty: e.target.value,
                  projectId: currentProjectId,
                })
              }
            />
          </ClaimsRow>

          <ClaimsRow label="Contract Clause" helper="Relevant clause reference for administration and adjudication.">
            <input
              type="text"
              value={claimForm.contractClause || ""}
              onChange={(e) =>
                setClaimForm({
                  ...claimForm,
                  contractClause: e.target.value,
                  projectId: currentProjectId,
                })
              }
            />
          </ClaimsRow>

          <ClaimsRow label="Supporting Reference" helper="Reference to letters, notices, programmes, instructions or other supporting documents.">
            <input
              type="text"
              value={claimForm.supportingReference || ""}
              onChange={(e) =>
                setClaimForm({
                  ...claimForm,
                  supportingReference: e.target.value,
                  projectId: currentProjectId,
                })
              }
            />
          </ClaimsRow>

          {/* Group: Quantum */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.12em", padding: "12px 0 4px", borderTop: "2px solid #f1f5f9", marginTop: 8 }}>
            Quantum — Time &amp; Cost
          </div>

          <ClaimsRow label="Days Claimed / Awarded" helper="Time claim entitlement used for revised date calculation on the home page.">
            <div style={{ display: "grid", gap: 10 }}>
              <input
                type="number"
                placeholder="Days claimed"
                value={claimForm.daysClaimed || ""}
                onChange={(e) =>
                  setClaimForm({ ...claimForm, daysClaimed: e.target.value, projectId: currentProjectId })
                }
              />
              <input
                type="number"
                placeholder="Days awarded"
                value={claimForm.daysAwarded || ""}
                onChange={(e) =>
                  setClaimForm({ ...claimForm, daysAwarded: e.target.value, projectId: currentProjectId })
                }
              />
            </div>
          </ClaimsRow>

          <ClaimsRow
            label="P&G Rate Per Day"
            helper="Daily preliminaries and general rate used to calculate the claim value for payment certificates. Set this to allow day-based allocation in the certificate builder."
          >
            <div style={{ display: "grid", gap: 10 }}>
              <input
                type="number"
                step="0.01"
                placeholder="e.g. 25000.00"
                value={claimForm.pgRatePerDay || ""}
                onChange={(e) => {
                  const rate = numberOrZero(e.target.value);
                  const daysC = numberOrZero(claimForm.daysClaimed);
                  const daysA = numberOrZero(claimForm.daysAwarded);
                  setClaimForm({
                    ...claimForm,
                    pgRatePerDay: e.target.value,
                    pgRateDay: e.target.value,
                    pgClaimed: daysC > 0 && rate > 0 ? String(Math.round(daysC * rate * 100) / 100) : claimForm.pgClaimed || "",
                    pgAwarded: daysA > 0 && rate > 0 ? String(Math.round(daysA * rate * 100) / 100) : claimForm.pgAwarded || "",
                    projectId: currentProjectId,
                  });
                }}
              />
              {numberOrZero(claimForm.pgRatePerDay) > 0 && numberOrZero(claimForm.daysAwarded) > 0 && (
                <div style={{
                  padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                  background: "#f0fdf4", color: "#166534",
                  border: "1px solid #9FDACB",
                }}>
                  Authorised value: {currency(numberOrZero(claimForm.pgRatePerDay) * numberOrZero(claimForm.daysAwarded))}
                  {" "}({claimForm.daysAwarded} days × {currency(claimForm.pgRatePerDay)}/day)
                </div>
              )}
            </div>
          </ClaimsRow>

          <ClaimsRow label="P&G Claimed / Awarded" helper="Auto-calculated from P&G Rate Per Day × days, or override manually. This value is for record keeping; day-based allocation in certificates uses the rate above.">
            <div style={{ display: "grid", gap: 10 }}>
              <input
                type="number"
                placeholder="P&G claimed"
                value={claimForm.pgClaimed || ""}
                onChange={(e) =>
                  setClaimForm({ ...claimForm, pgClaimed: e.target.value, projectId: currentProjectId })
                }
              />
              <input
                type="number"
                placeholder="P&G awarded"
                value={claimForm.pgAwarded || ""}
                onChange={(e) =>
                  setClaimForm({ ...claimForm, pgAwarded: e.target.value, projectId: currentProjectId })
                }
              />
            </div>
          </ClaimsRow>

          <ClaimsRow label="Direct Cost Claimed / Awarded" helper="Any direct cost element over and above P&G.">
            <div style={{ display: "grid", gap: 10 }}>
              <input
                type="number"
                placeholder="Direct cost claimed"
                value={claimForm.directCostClaimed || ""}
                onChange={(e) =>
                  setClaimForm({
                    ...claimForm,
                    directCostClaimed: e.target.value,
                    projectId: currentProjectId,
                  })
                }
              />
              <input
                type="number"
                placeholder="Direct cost awarded"
                value={claimForm.directCostAwarded || ""}
                onChange={(e) =>
                  setClaimForm({
                    ...claimForm,
                    directCostAwarded: e.target.value,
                    projectId: currentProjectId,
                  })
                }
              />
            </div>
          </ClaimsRow>

          {/* Group: Determination & Close-Out */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.12em", padding: "12px 0 4px", borderTop: "2px solid #f1f5f9", marginTop: 8 }}>
            Determination &amp; Close-Out
          </div>

          <ClaimsRow label="Engineer Recommendation" helper="Capture recommendation before final ruling.">
            <textarea
              value={claimForm.engineerRecommendation || ""}
              onChange={(e) =>
                setClaimForm({
                  ...claimForm,
                  engineerRecommendation: e.target.value,
                  projectId: currentProjectId,
                })
              }
            />
          </ClaimsRow>

          <ClaimsRow label="Close-Out Remarks" helper="Final remarks once the claim is concluded.">
            <textarea
              value={claimForm.closeOutRemarks || ""}
              onChange={(e) =>
                setClaimForm({
                  ...claimForm,
                  closeOutRemarks: e.target.value,
                  projectId: currentProjectId,
                })
              }
            />
          </ClaimsRow>

          {/* Group: Notifications */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.12em", padding: "12px 0 4px", borderTop: "2px solid #f1f5f9", marginTop: 8 }}>
            Notification Schedule
          </div>

          <ClaimsRow label="Notifications" helper="Choose how many reminder notifications you want to track for this claim.">
            <input
              type="number"
              min="0"
              value={claimForm.notificationCount || "0"}
              onChange={(e) => updateNotificationCount(e.target.value)}
            />
          </ClaimsRow>

          {buildNotificationArray(notificationCount, notifications).map((notice, index) => (
            <ClaimsRow
              key={notice.id}
              label={notice.label}
              helper={
                claimStillValid
                  ? "Notification is active while the claim is still valid."
                  : "Claim is no longer valid or active. Notification remains for record purposes only."
              }
            >
              <div style={{ display: "grid", gap: 10 }}>
                <input
                  type="text"
                  placeholder="Notification label"
                  value={notice.label}
                  onChange={(e) => updateNotificationField(index, "label", e.target.value)}
                />
                <input
                  type="date"
                  value={notice.notifyDate || ""}
                  onChange={(e) => updateNotificationField(index, "notifyDate", e.target.value)}
                />
                <select
                  value={notice.sent || "No"}
                  onChange={(e) => updateNotificationField(index, "sent", e.target.value)}
                >
                  <option value="No">Not Sent</option>
                  <option value="Yes">Sent</option>
                </select>
              </div>
            </ClaimsRow>
          ))}
        </div>

        <div style={SECTION_STYLES.actionRow}>
          <button onClick={handleAddOrUpdateClaim} style={SECTION_STYLES.primaryButton}>
            {editingClaimId ? "Update Claim" : "Save Claim"}
          </button>
          {editingClaimId ? (
            <button onClick={clearClaimForm} style={SECTION_STYLES.secondaryButton}>
              Cancel Edit
            </button>
          ) : null}
        </div>
      </div>

      {/* ── CLAIMS REGISTER TABLE ────────────────────────────────────────── */}
      <div style={SECTION_STYLES.sectionCard}>
        <div style={SECTION_STYLES.sectionHeader}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 4 }}>Live Register</div>
            <h3 style={SECTION_STYLES.sectionTitle}>Claims Register</h3>
            <p style={SECTION_STYLES.sectionSubtitle}>
              Real-time view of all claims with time-bar compliance, notification status, quantum, and awarded entitlements.
            </p>
          </div>
        </div>

        {/* Inline filter controls */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10, alignItems: "end" }}>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Search</label>
            <input
              type="text"
              value={claimSearch}
              onChange={(e) => setClaimSearch(e.target.value)}
              placeholder="Claim number, title, clause…"
              style={{ width: "100%", boxSizing: "border-box", borderRadius: 7, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: 13, outline: "none", background: "#f8fafc" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Status</label>
            <select
              value={claimStatusFilter}
              onChange={(e) => setClaimStatusFilter(e.target.value)}
              style={{ borderRadius: 7, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: 13, background: "#f8fafc", cursor: "pointer", minWidth: 140 }}
            >
              <option value="All">All Statuses</option>
              <option value="In Review">In Review</option>
              <option value="Awarded">Awarded</option>
              <option value="Rejected">Rejected</option>
              <option value="Time Barred">Time Barred</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Sort</label>
            <select value={claimSortBy} onChange={(e) => setClaimSortBy(e.target.value)} style={{ borderRadius: 7, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: 13, background: "#f8fafc", cursor: "pointer", minWidth: 180 }}>
              <option value="claimDate_desc">Claim Date (Newest)</option>
              <option value="claimDate_asc">Claim Date (Oldest)</option>
              <option value="claimNo_asc">Claim No (Asc)</option>
              <option value="claimNo_desc">Claim No (Desc)</option>
              <option value="status_asc">Status A–Z</option>
              <option value="status_desc">Status Z–A</option>
            </select>
          </div>
        </div>

        {/* Enterprise table */}
        <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10, marginTop: 4 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                {["Claim No", "Title / Basis", "Latest Notice", "Submitted", "Time-Bar", "Notices Due", "Days Cl.", "Days Aw.", "P&G Rate/Day", "P&G Claimed", "Status", "Ruling Date", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "9px 12px", textAlign: h === "Actions" ? "center" : ["Days Cl.", "Days Aw.", "P&G Rate/Day", "P&G Claimed"].includes(h) ? "right" : "left", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap", borderRight: "1px solid #f1f5f9" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredClaims.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ textAlign: "center", color: "#9ca3af", padding: "32px 16px", fontSize: 13 }}>
                    No claims found for the selected filters.
                  </td>
                </tr>
              ) : (
                filteredClaims.map((row, idx) => {
                  const daysRemaining = getClaimDaysRemaining(
                    row.latestNotificationDate,
                    row.claimDate,
                    contractType
                  );

                  const notificationsDue = (Array.isArray(row.notifications) ? row.notifications : []).filter(
                    (notice) => {
                      if (!notice?.notifyDate) return false;
                      if (String(row.status || "") === "Rejected" || String(row.status || "") === "Time Barred") return false;
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const notifyDate = new Date(notice.notifyDate);
                      if (Number.isNaN(notifyDate.getTime())) return false;
                      notifyDate.setHours(0, 0, 0, 0);
                      return notifyDate <= today && String(notice.sent || "No") !== "Yes";
                    }
                  ).length;

                  const td = (extra = {}) => ({
                    padding: "11px 12px",
                    borderBottom: "1px solid #f1f5f9",
                    borderRight: "1px solid #f8fafc",
                    verticalAlign: "middle",
                    background: idx % 2 === 0 ? "#fff" : "#fafafa",
                    ...extra,
                  });

                  return (
                    <tr key={row.id}>
                      <td style={td({ fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" })}>{row.claimNo || "—"}</td>
                      <td style={td({ maxWidth: 200, whiteSpace: "normal" })}>
                        <div style={{ fontWeight: 600, color: "#0f172a", lineHeight: 1.3, fontSize: 13 }}>{row.title || "—"}</div>
                        {row.contractClause && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Cl. {row.contractClause}</div>}
                        {row.claimType && <div style={{ fontSize: 10, color: "#94a3b8" }}>{row.claimType}</div>}
                      </td>
                      <td style={td({ whiteSpace: "nowrap", color: "#374151", fontSize: 12 })}>{row.latestNotificationDate || "—"}</td>
                      <td style={td({ whiteSpace: "nowrap", color: "#374151", fontSize: 12 })}>{row.claimDate || "—"}</td>
                      <td style={td({ whiteSpace: "nowrap" })}>
                        <span style={{ ...getClaimCountdownStyle(daysRemaining), display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                          {getClaimTimeBarLabel(row.latestNotificationDate, row.claimDate, contractType)}
                        </span>
                      </td>
                      <td style={td({ textAlign: "right" })}>
                        {notificationsDue > 0 ? (
                          <span style={{ display: "inline-block", padding: "3px 10px", background: "#fff7ed", color: "#ea580c", fontWeight: 700, borderRadius: 6, fontSize: 12, border: "1px solid #fed7aa" }}>
                            {notificationsDue}
                          </span>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: 12 }}>0</span>
                        )}
                      </td>
                      <td style={td({ textAlign: "right", fontWeight: 600 })}>{numberOrZero(row.daysClaimed) || "—"}</td>
                      <td style={td({ textAlign: "right", fontWeight: 600, color: "#15803d" })}>{numberOrZero(row.daysAwarded) || "—"}</td>
                      <td style={td({ textAlign: "right", fontSize: 12 })}>{numberOrZero(row.pgRatePerDay || row.pgRateDay) > 0 ? currency(row.pgRatePerDay || row.pgRateDay) : "—"}</td>
                      <td style={td({ textAlign: "right", fontWeight: 600 })}>{numberOrZero(row.pgClaimed) > 0 ? currency(row.pgClaimed) : "—"}</td>
                      <td style={td({ whiteSpace: "nowrap" })}>
                        <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 11, ...statusStyle(row.status) }}>
                          {row.status || "—"}
                        </span>
                      </td>
                      <td style={td({ whiteSpace: "nowrap", fontSize: 12, color: "#374151" })}>{row.proposedRulingDate || computeProposedRulingDate(row.claimDate) || "—"}</td>
                      <td style={td({ textAlign: "center" })}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                          <button
                            onClick={() => handleViewItem(`Claim ${row.claimNo}`, row)}
                            style={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
                          >View</button>
                          <button
                            onClick={() => handleEditClaim(row)}
                            style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
                          >Edit</button>
                          <button
                            onClick={() => handleDeleteClaim(row.id)}
                            style={SECTION_STYLES.dangerButton}
                          >Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filteredClaims.length > 0 && (
              <tfoot>
                <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0", fontWeight: 700 }}>
                  <td colSpan={6} style={{ padding: "9px 12px", fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    {filteredClaims.length} claim{filteredClaims.length !== 1 ? "s" : ""}
                  </td>
                  <td style={{ padding: "9px 12px", textAlign: "right", fontSize: 13 }}>
                    {filteredClaims.reduce((s, r) => s + numberOrZero(r.daysClaimed), 0)}
                  </td>
                  <td style={{ padding: "9px 12px", textAlign: "right", fontSize: 13, color: "#15803d" }}>
                    {filteredClaims.reduce((s, r) => s + numberOrZero(r.daysAwarded), 0)}
                  </td>
                  <td style={{ padding: "9px 12px" }} />
                  <td style={{ padding: "9px 12px", textAlign: "right", fontSize: 13 }}>
                    {currency(filteredClaims.reduce((s, r) => s + numberOrZero(r.pgClaimed), 0))}
                  </td>
                  <td colSpan={3} style={{ padding: "9px 12px" }} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── AWARDED TIME IMPACT ───────────────────────────────────────────── */}
      <div style={SECTION_STYLES.sectionCard}>
        <div style={SECTION_STYLES.sectionHeader}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 4 }}>Extension of Time</div>
            <h3 style={SECTION_STYLES.sectionTitle}>Awarded Time Impact</h3>
            <p style={SECTION_STYLES.sectionSubtitle}>
              Cumulative awarded calendar days feeding into the revised practical completion date.
            </p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
          {[
            { label: "Original Completion Date", value: formatDate(estimatedEndDate), color: "#0f172a" },
            { label: "Total Awarded Days", value: `${awardedDaysTotal} days`, color: "#6366f1" },
            { label: "Revised Completion Date", value: formatDate(calculatedRevisedDate), color: calculatedRevisedDate && calculatedRevisedDate !== formatDate(estimatedEndDate) ? "#dc2626" : "#15803d" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background: "#fff", padding: "18px 22px", borderRight: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: "-0.02em" }}>{value || "—"}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CERTIFICATE P&G ALLOCATIONS ───────────────────────────────────── */}
      {projectClaims.some(c => (c.pgRatePerDay || c.pgRateDay) && numberOrZero(c.daysAwarded || c.daysClaimed) > 0) && (
        <div style={SECTION_STYLES.sectionCardLast}>
          <div style={SECTION_STYLES.sectionHeader}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 4 }}>Certificate Allocation</div>
              <h3 style={SECTION_STYLES.sectionTitle}>P&amp;G Claim Utilisation Register</h3>
              <p style={SECTION_STYLES.sectionSubtitle}>
                Running balance of days and amounts certified against each approved P&amp;G claim across all payment certificates.
              </p>
            </div>
          </div>

          <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 860 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  {["Claim No", "Title", "P&G Rate/Day", "Days Awarded", "Total Authorised", "Days Certified", "Amt Certified", "Days Remaining", "Amt Remaining", "% Utilised"].map(h => (
                    <th key={h} style={{
                      padding: "9px 12px", textAlign: ["Days Awarded","Total Authorised","Days Certified","Amt Certified","Days Remaining","Amt Remaining","% Utilised","P&G Rate/Day"].includes(h) ? "right" : "left",
                      fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#6b7280", whiteSpace: "nowrap", borderRight: "1px solid #f1f5f9"
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projectClaims
                  .filter(c => (c.pgRatePerDay || c.pgRateDay) || numberOrZero(c.daysAwarded || c.daysClaimed) > 0)
                  .map((claim, idx) => {
                    const rate         = numberOrZero(claim.pgRatePerDay || claim.pgRateDay);
                    const daysAwarded  = numberOrZero(claim.daysAwarded || claim.daysClaimed);
                    const totalAuth    = rate * daysAwarded;
                    const projectCerts = (certificates || []).filter(c => !currentProjectId || c.projectId === currentProjectId);
                    const certifiedDays = projectCerts.reduce((sum, cert) => {
                      const matched = (cert.claimsItems || []).find(ci => ci.claimId === claim.id);
                      return sum + numberOrZero(matched?.daysThisCertificate);
                    }, 0);
                    const certifiedAmt  = projectCerts.reduce((sum, cert) => {
                      const matched = (cert.claimsItems || []).find(ci => ci.claimId === claim.id);
                      return sum + numberOrZero(matched?.amountThisCertificate);
                    }, 0);
                    const remainingDays = daysAwarded - certifiedDays;
                    const remainingAmt  = totalAuth - certifiedAmt;
                    const pctUsed       = daysAwarded > 0 ? Math.round((certifiedDays / daysAwarded) * 100) : 0;
                    const isExhausted   = remainingDays <= 0 && daysAwarded > 0;
                    const isLow         = remainingDays > 0 && remainingDays <= 5;
                    const rowBg         = isExhausted ? "#fef2f2" : isLow ? "#fff7ed" : idx % 2 === 0 ? "#fff" : "#fafafa";
                    const amtColor      = isExhausted ? "#dc2626" : isLow ? "#ea580c" : "#15803d";
                    const td = { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", borderRight: "1px solid #f8fafc", verticalAlign: "middle", background: rowBg };
                    return (
                      <tr key={claim.id}>
                        <td style={{ ...td, fontWeight: 700, color: "#0f172a" }}>{claim.claimNo || "—"}</td>
                        <td style={{ ...td, maxWidth: 180, whiteSpace: "normal" }}>
                          <div style={{ fontWeight: 600, lineHeight: 1.3, fontSize: 13 }}>{claim.title || claim.detail || "—"}</div>
                        </td>
                        <td style={{ ...td, textAlign: "right" }}>{rate > 0 ? currency(rate) : "—"}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{daysAwarded || "—"}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{totalAuth > 0 ? currency(totalAuth) : "—"}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{certifiedDays}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{currency(certifiedAmt)}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700, color: amtColor }}>
                          {isExhausted ? <span style={{ padding: "2px 8px", background: "#fee2e2", borderRadius: 5, fontSize: 11 }}>0 ⚠</span> : remainingDays}
                        </td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700, color: amtColor }}>{currency(Math.max(0, remainingAmt))}</td>
                        <td style={td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, height: 6, borderRadius: 3, background: "#e2e8f0", overflow: "hidden", minWidth: 60 }}>
                              <div style={{ height: "100%", width: `${Math.min(100, pctUsed)}%`, background: isExhausted ? "#dc2626" : isLow ? "#ea580c" : "#15803d", borderRadius: 3, transition: "width 0.3s" }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: isExhausted ? "#dc2626" : "#374151", minWidth: 36, textAlign: "right" }}>{pctUsed}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
export default React.memo(ClaimsSection);
